import { getUserDetails } from "../../../../app/api/getUserDetails";
import { BaseAction } from "../../../../core/actions/BaseAction";
import { updateRecoilState } from "../../../../Orchestrator/Recoils/Events/Recoil";
import { IAdditionalPassenger, IApiResponse, IUserResponse } from "../../../../Orchestrator/Types/Index";
import { getUserDetailsAction } from "../../MyAccount/GetUserDetailsAction";

class GetPassengerDetailsAction extends BaseAction<{ customerData: IUserResponse, savedPassenger: IAdditionalPassenger[] }, void> {
    // Coalesce concurrent execute() calls — Topbar's isAuthenticated effect and
    // PassengerDetailsInitStep both fire on checkout mount.
    protected override deduplicate = true;

    // BaseAction._runWithCatch clears this._result every run(), so keep a
    // session-scoped cache. invalidate() drops it on logout / profile change.
    private _cached: { customerData: IUserResponse, savedPassenger: IAdditionalPassenger[] } | null = null;

    invalidate(): void {
        this._cached = null;
        getUserDetailsAction.invalidate();
    }

    override async run(): Promise<{ customerData: IUserResponse, savedPassenger: IAdditionalPassenger[] } | null> {
        let userResponses: IUserResponse | null = null;
        try {
            if (this._cached != null) {
                // clearBookingFlowAtoms() wipes savedPassengerDetailAtom (and
                // checkoutUserDataAtom) at the start of every new booking flow.
                // Re-seed from the cache so the dropdown re-populates without
                // a redundant /me round-trip.
                const cachedUser = this._cached.customerData;
                updateRecoilState("userDataAtom", {
                    ...cachedUser,
                    firstName: cachedUser.firstName === "-" ? "" : cachedUser.firstName,
                    lastName: cachedUser.lastName === "-" ? "" : cachedUser.lastName,
                });
                updateRecoilState("savedPassengerDetailAtom", this._cached.savedPassenger);
                updateRecoilState("checkoutUserDataAtom", {
                    ...cachedUser,
                    firstName: cachedUser.firstName === "-" ? "" : cachedUser.firstName,
                    lastName: cachedUser.lastName === "-" ? "" : cachedUser.lastName,
                });
                return this._cached;
            }
            await getUserDetailsAction.execute();

                const userResponse = getUserDetailsAction.result;

                if (userResponse) {
                    userResponses = userResponse;
                    const updatedUserData: IUserResponse = {
                        ...userResponses,
                        firstName: userResponses.firstName === "-" ? "" : userResponses.firstName,
                        lastName: userResponses.lastName === "-" ? "" : userResponses.lastName,
                    };
                    updateRecoilState("userDataAtom", updatedUserData);

                    const rawSavedPassengers = [
                        // Only add the firstName and lastName if they are not "-"
                        ...(userResponse?.firstName !== "-" && userResponse?.lastName !== "-"
                            ? [{
                                firstName: userResponse?.firstName || '',
                                lastName: userResponse?.lastName || '',
                                birthdate: userResponse?.birthdate || '',
                                isSelected: false,
                                id: "0",
                            }]
                            : []
                        ),
                        ...(userResponse?.additionalPassengers?.map((p, index) => ({
                            ...p,
                            isSelected: false,
                            id: (index + 1).toString(),
                        })) || [])
                    ];

                    // Client-side dedupe: the backend occasionally returns
                    // duplicate additionalPassengers (same firstName+lastName+birthdate)
                    // which surfaces in the checkout passenger dropdown as repeated
                    // rows. Key-by normalized identity; the first occurrence wins
                    // so the primary user ("Me", id "0") always comes through.
                    const dedupeKey = (p: IAdditionalPassenger) =>
                        `${(p.firstName || "").trim().toLowerCase()}|${(p.lastName || "").trim().toLowerCase()}|${p.birthdate || ""}`;
                    const seen = new Set<string>();
                    const savedPassengers: IAdditionalPassenger[] = [];
                    for (const p of rawSavedPassengers) {
                        const key = dedupeKey(p);
                        // Skip entries with no identifying info at all (shouldn't happen,
                        // but if it does they'd all collapse into one empty key).
                        if (!p.firstName && !p.lastName) continue;
                        if (seen.has(key)) continue;
                        seen.add(key);
                        savedPassengers.push(p);
                    }

                    updateRecoilState("savedPassengerDetailAtom", savedPassengers)

                    updateRecoilState("checkoutUserDataAtom", {
                        ...userResponse,
                        firstName: userResponse.firstName === "-" ? "" : userResponse.firstName,
                        lastName: userResponse.lastName === "-" ? "" : userResponse.lastName,
                    })

                    let responseData = {
                        customerData: userResponse,
                        savedPassenger: savedPassengers
                    }
                    this._cached = responseData;
                    return responseData;
                } else {
                    console.warn("No userdetail available.");
                }
            //}
        } catch (error: any) {
            console.error("Error fetching userdetail:", error);
        }
        return null;
    }
}

export const getPassengerDetailsAction = new GetPassengerDetailsAction()


